import React from "react";

import {
  CommonProps,
  components,
  ControlProps,
  MenuListProps,
  MenuProps,
  OptionProps
} from "react-select";

import { useTheme } from "../../../context/theme";
import {
  typographyBodyLineHeightMap,
  typographyBodySizeMap
} from "../../../styles/typography/typography";
import { Box, Inline } from "../../layout";
import { Var } from "../../other";
import Checkbox from "../Checkbox";
import DropdownControl from "./components/DropdownControl";
import MultiValueLabel from "./components/MultiValueLabel";
import SearchInput from "./components/SearchInput";
import { SelectAllOption } from "./hooks/useSelectAll";
import {
  CustomComponentProps,
  CustomLogicProps,
  MultiSelectProps,
  Option,
  OptionGroup
} from "./types";

export default {
  Option: CustomOption,
  Menu: CustomMenu,
  MenuList: CustomMenuList,
  Control: CustomControl
};

/**
 * This is the only way I could find to type these components and reflect
 * the fact that any props passed to the top-level Select that are in
 * addition to the officially accepted props, will get passed down
 * to the custom components via the `selectProps` prop.
 *
 * Note that although this means these components are safely typed
 * internally, the components, and any additional props, cause
 * type warnings when passed to the top-level Select. These
 * type warnings have been ignored in `MultiSelectDropdown`.
 *
 * See: https://github.com/JedWatson/react-select/issues/4804
 */
type WithCustomProps<
  ComponentProps extends CommonProps<Option, true, OptionGroup>
> = ComponentProps & {
  selectProps: ComponentProps["selectProps"] &
    CustomComponentProps &
    CustomLogicProps &
    MultiSelectProps;
};

function CustomOption({
  children,
  ...props
}: WithCustomProps<OptionProps<Option, true, OptionGroup>>) {
  const { dataNoTranslateOption } = props.selectProps;
  const isSelectAllOption = children === SelectAllOption.label;

  const onSelectOption = () => {
    props.selectOption && props.selectOption(props.data);
  };

  const theme = useTheme();

  return (
    <components.Option {...props}>
      <Box display="flex" flexDirection="row" alignItems="center">
        <Checkbox
          checked={props.isSelected}
          name={props.label}
          onChange={onSelectOption}
          disabled={props.isDisabled}
        >
          <Box
            cursor={props.isDisabled ? "default" : "pointer"}
            dataNoTranslate={
              (dataNoTranslateOption && !isSelectAllOption) || undefined
            }
            color={
              props.isSelected
                ? theme["primary-high-content"]
                : theme["neutral-high-content"]
            }
          >
            {children}
          </Box>
        </Checkbox>
      </Box>
    </components.Option>
  );
}

function CustomMenuList({
  children,
  ...props
}: WithCustomProps<MenuListProps<Option, true, OptionGroup>>) {
  const { listHeader, dataNoTranslateListHeader } = props.selectProps;
  return (
    <components.MenuList {...props}>
      {listHeader && (
        <Box paddingY="large" paddingX="large">
          <Box paddingX="small">
            <Box
              dataNoTranslate={dataNoTranslateListHeader}
              fontWeight="bold"
              {...typographyBodySizeMap.small}
              {...typographyBodyLineHeightMap.small}
            >
              {listHeader}
            </Box>
          </Box>
        </Box>
      )}
      {children}
    </components.MenuList>
  );
}

function CustomMenu({
  children,
  ...props
}: WithCustomProps<MenuProps<Option, true, OptionGroup>>) {
  const { onChangeSearch, searchValue, isSearchable } = props.selectProps;
  return (
    <components.Menu {...props}>
      {isSearchable && (
        <SearchInput
          autoFocus
          onChangeSearch={onChangeSearch}
          searchValue={searchValue}
        />
      )}
      {children}
    </components.Menu>
  );
}

function CustomControl({
  children,
  getValue,
  ...props
}: WithCustomProps<ControlProps<Option, true, OptionGroup>>) {
  const {
    isDisabled,
    isSearchable,
    placeholder,
    ariaLabel,
    onClickMenu,
    menuIsOpen,
    removeAll,
    removeItem,
    fixedValues = [],
    maxMultiValueLabels,
    dataNoTranslateOption,
    allSelected,
    disableAllSelected
  } = props.selectProps;

  const value = getValue();

  function getLabel() {
    if (!disableAllSelected && allSelected)
      return (
        <MultiValueLabel removeable={!isDisabled} onClickRemove={removeAll}>
          All selected
        </MultiValueLabel>
      );

    if (maxMultiValueLabels && value.length > maxMultiValueLabels)
      return (
        <MultiValueLabel removeable={!isDisabled} onClickRemove={removeAll}>
          <Var>{value.length}</Var> selected
        </MultiValueLabel>
      );

    return value.map(({ label, value }: { label: string; value: string }) => (
      <MultiValueLabel
        key={value}
        dataNoTranslate={dataNoTranslateOption}
        removeable={!fixedValues.includes(value) && !isDisabled}
        onClickRemove={() => removeItem(value)}
      >
        {formatLabel(label)}
      </MultiValueLabel>
    ));
  }

  return (
    <DropdownControl
      searchable={isSearchable}
      disabled={isDisabled}
      ariaLabel={ariaLabel}
      onClick={onClickMenu}
      menuIsOpen={menuIsOpen}
    >
      {value.length ? (
        <Inline space="small">{getLabel()}</Inline>
      ) : (
        <Box>{placeholder}</Box>
      )}
    </DropdownControl>
  );
}

const MAX_LABEL_LENGTH = 20;

function formatLabel(label: string) {
  return label.length > MAX_LABEL_LENGTH
    ? label.substring(0, MAX_LABEL_LENGTH).concat("...")
    : label;
}
